import { AlertRepository } from "../../repositories/AlertRrepository";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../common/CommonActions";
import { EloMessageRepository } from "../../repositories/EloMessageRepository";
import { triggerRerender } from "../../common/CommonActions";
import { ErrorMessageResolver } from "../../common/ErrorMessageResolver";
import { AuthRepository } from "../../repositories/AuthRepository";

const getDefaultEloMessage = () => ({
  correlationId: "",
  internalReference: "",
  email: "",
  informationsAppairage: {
    sensTraversee: "IMPORT",
    typeCamion: false,
    estTIRATA: false,
    estSPS: false,
    estProduitPeche: false,
    estPostal: false,
    estEmballageVide: false,
    possedeContratTransport: false,
  },
  identifiantsDeclaration: [],
  ics2EnsDeclarations: [],
  importTransitDeclarations: [],
  organization: null,
  errors: [],
});

function FieldTitle({ children, hint }) {
  return (
    <Typography
      variant="caption"
      sx={{ fontWeight: 600, mb: 0.75, display: "block" }}
    >
      {children}
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <Icon
            fontSize="small"
            color="action"
            sx={{ ml: 0.5, verticalAlign: "middle" }}
          >
            help_outline
          </Icon>
        </Tooltip>
      )}
    </Typography>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography variant="h6" gutterBottom>
      {children}
    </Typography>
  );
}

function IdentifierSection({
  title,
  hint,
  currentValue,
  setCurrentValue,
  items,
  onAdd,
  onRemove,
  helperTextEmpty,
  disabled,
}) {
  const normalizedValue = useMemo(
    () =>
      currentValue
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ""),
    [currentValue],
  );

  const isDuplicate = items.includes(normalizedValue);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <Box>
          <FieldTitle hint={hint}>{title}</FieldTitle>
          <Typography variant="body2" color="text.secondary">
            {helperTextEmpty}
          </Typography>
        </Box>

        <Chip
          color="primary"
          size="small"
          variant="outlined"
          label={`${items.length} added`}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <TextField
            label={title}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            disabled={disabled}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAdd();
              }
            }}
            fullWidth
            size="small"
            error={Boolean(normalizedValue) && isDuplicate}
            helperText={
              Boolean(normalizedValue) && isDuplicate
                ? "This identifier is already added"
                : "Press Enter or click Add"
            }
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAdd}
            fullWidth
            sx={{ height: "40px" }}
            disabled={
              disabled ||
              !normalizedValue ||
              normalizedValue.length < 5 ||
              isDuplicate
            }
          >
            Add
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        {items.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              No identifiers added yet.
            </Typography>
          </Grid>
        ) : (
          items.map((identifier, index) => (
            <Grid item xs={12} md={6} key={`${identifier}-${index}`}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Identifier
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {identifier}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemove(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>
    </Paper>
  );
}

export const EloMessageForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const isMobile = useMediaQuery("(max-width:600px)");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [eloMessage, setEloMessage] = useState(getDefaultEloMessage());
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [formDataErrors, setFormDataErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await AlertRepository.getAllActiveByType({ type: "ELO" });
        setAlerts(response.data);
      } catch (error) {
        console.error("Failed to fetch alerts: ", error);
      }
    };
    fetchAlerts();
  }, []);

  const hasErrorAlert = alerts?.some((alert) => alert?.severity === "error");

  const [popup, setPopup] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [importEnsInput, setImportEnsInput] = useState("");
  const [importTransitInput, setImportTransitInput] = useState("");
  const [exportInput, setExportInput] = useState("");

  const isEmptyChecked = Boolean(eloMessage.informationsAppairage?.typeCamion);
  const isPostalChecked = Boolean(eloMessage.informationsAppairage?.estPostal);
  const isAtaChecked = Boolean(eloMessage.informationsAppairage?.estTIRATA);
  const isEmptyPackaging = Boolean(eloMessage.informationsAppairage?.estEmballageVide);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
    },
  };

  const currentDirection =
    eloMessage?.informationsAppairage?.sensTraversee || "IMPORT";

  const getSafeArray = (value) => (Array.isArray(value) ? value : []);

  /** -------- Duplicate consignment -------- */

  const allowedStatusesDuplicate = ["ERROR", "DRAFT", "COMPLETED"];
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);

  const handleDuplicateDialogOpen = () => {
    setOpenDuplicateDialog(true);
  };

  const handleCloseDuplicateDialog = () => {
    setOpenDuplicateDialog(false);
  };

  const duplicateDeclaration = () => {
    if (!eloMessage?.id) {
      console.log("No consignment selected");
      return;
    }
    setLoading(true);
    EloMessageRepository.duplicate(eloMessage.id)
      .then((response) => {
        dispatch(
          notifyShowSuccessMessage("Declaration duplicated successfully"),
        );

        const id = response.data?.id;
        if (!id) {
          throw new Error("Duplicate consignment returned no ID");
        }

        handleCloseDuplicateDialog();

        const basePath = window.location.pathname.replace(/\/edit\/[^/]+$/, "");
        const url = basePath + "/edit/" + id;
        window.open(url, "_blank");
        getEloMessage();
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDraft = () => {
    setLoading(true);
    setFormDataErrors(null);

    const payload = {
      ...eloMessage,
      identifiantsDeclaration: getSafeArray(eloMessage.identifiantsDeclaration),
      ics2EnsDeclarations: getSafeArray(eloMessage.ics2EnsDeclarations),
      importTransitDeclarations: getSafeArray(
        eloMessage.importTransitDeclarations,
      ),
    };

    const request = isEditMode
      ? EloMessageRepository.updateDraftDeclaration(payload)
      : EloMessageRepository.createDraftDeclaration(
          payload,
          eloMessage.organization?.id,
        );

    request
      .then(() => {
        dispatch(triggerRerender());
        setPopup({
          open: true,
          message: "Draft saved successfully!",
          severity: "success",
        });
        navigateBack();
      })
      .catch((err) => {
        console.error(err);
        const resolvedError = ErrorMessageResolver.resolve(err);
        setFormDataErrors(resolvedError);
        setPopup({
          open: true,
          message: resolvedError || "Something went wrong",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  };


  const normalizeIdentifier = (value) =>
    value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

  useEffect(() => {
    if (isEditMode) {
      getEloMessage();
    } else {
      const currentUser = AuthRepository.getUserDetails();
      const defaultMessage = getDefaultEloMessage();

      if (currentUser?.organization) {
        defaultMessage.organization = currentUser.organization;
        setOrganizationSearch(currentUser.organization.nameEn || "");
      }

      setEloMessage(defaultMessage);
    }
  }, [id]);

  const handleChangeEloMessage = (name, value) => {
    setEloMessage((prev) => _.set({ ...prev }, name, value));
  };

  const getEloMessage = () => {
    setLoading(true);
    EloMessageRepository.getById(id)
      .then((res) => {
        const rawData = res.data || getDefaultEloMessage();

        const data =
          rawData && rawData.id === undefined && rawData.present !== undefined
            ? rawData.value || getDefaultEloMessage()
            : rawData;

        const normalizedData = {
          ...getDefaultEloMessage(),
          ...data,
          informationsAppairage: {
            ...getDefaultEloMessage().informationsAppairage,
            ...(data?.informationsAppairage || {}),
          },
          identifiantsDeclaration: getSafeArray(data?.identifiantsDeclaration),
          ics2EnsDeclarations: getSafeArray(data?.ics2EnsDeclarations),
          importTransitDeclarations: getSafeArray(
            data?.importTransitDeclarations,
          ),
          errors: Array.isArray(data?.errors) ? data.errors : [],
        };

        if (
          normalizedData?.informationsAppairage?.sensTraversee === "IMPORT" &&
          normalizedData.ics2EnsDeclarations.length === 0 &&
          normalizedData.importTransitDeclarations.length === 0 &&
          normalizedData.identifiantsDeclaration.length > 0
        ) {
          normalizedData.ics2EnsDeclarations = [
            ...normalizedData.identifiantsDeclaration,
          ];
        }

        setEloMessage(normalizedData);
        setOrganizationSearch(normalizedData?.organization?.nameEn || "");
      })
      .catch((err) => {
        console.error(err);
        setFormDataErrors("Failed to load Elo Message.");
        setPopup({
          open: true,
          message: "Failed to load Elo Message.",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const addToArrayField = (fieldName, inputValue, setInput) => {
    const normalized = normalizeIdentifier(inputValue);
    const currentItems = getSafeArray(eloMessage?.[fieldName]);

    if (!normalized || normalized.length < 5) return;
    if (currentItems.includes(normalized)) return;

    handleChangeEloMessage(fieldName, [...currentItems, normalized]);
    setInput("");
  };

  const removeFromArrayField = (fieldName, indexToRemove) => {
    const currentItems = getSafeArray(eloMessage?.[fieldName]);
    handleChangeEloMessage(
      fieldName,
      currentItems.filter((_, index) => index !== indexToRemove),
    );
  };

  const navigateBack = () => {
    const currentPath = window.location.pathname;

    if (isEditMode) {
      const basePath = currentPath.replace(/(\/edit\/[^/]+).*/, "$1");
      navigate(basePath.replace(/\/edit\/[^/]+$/, ""));
      return;
    }

    const basePath = currentPath.replace(/(\/new).*/, "$1");
    navigate(basePath.replace(/\/new$/, ""));
  };

  const handleClose = () => {
    navigateBack();
  };

  const handleDirectionChange = (newDirection) => {
    if (newDirection === currentDirection) return;

    const currentExportList = getSafeArray(eloMessage.identifiantsDeclaration);
    const currentEnsList = getSafeArray(eloMessage.ics2EnsDeclarations);
    const currentImportTransitList = getSafeArray(
      eloMessage.importTransitDeclarations,
    );

    if (newDirection === "IMPORT") {
      const hasSeparatedImportData =
        currentEnsList.length > 0 || currentImportTransitList.length > 0;

      const fallbackImportEns =
        !hasSeparatedImportData && currentExportList.length > 0
          ? [...currentExportList]
          : currentEnsList;

      setEloMessage((prev) => ({
        ...prev,
        informationsAppairage: {
          ...prev.informationsAppairage,
          sensTraversee: "IMPORT",
        },
        ics2EnsDeclarations: fallbackImportEns,
        importTransitDeclarations: currentImportTransitList,
        identifiantsDeclaration: currentExportList,
      }));
    } else {
      const mergedExportList = [
        ...currentEnsList,
        ...currentImportTransitList,
      ].filter((value, index, array) => array.indexOf(value) === index);

      setEloMessage((prev) => ({
        ...prev,
        informationsAppairage: {
          ...prev.informationsAppairage,
          sensTraversee: "EXPORT",
          estPostal: false,
          estEmballageVide: false,
          possedeContratTransport: false,
        },
        identifiantsDeclaration: mergedExportList,
        ics2EnsDeclarations: currentEnsList,
        importTransitDeclarations: currentImportTransitList,
      }));
    }

    setImportEnsInput("");
    setImportTransitInput("");
    setExportInput("");
  };

  const validateBeforeSubmit = () => {
    if (!eloMessage?.organization?.id) {
      return "Client is required.";
    }

    if(!eloMessage?.internalReference){
      return "Truck Plate Number is required.";
    }

    if (currentDirection === "IMPORT") {
      if (
        getSafeArray(eloMessage.ics2EnsDeclarations).length === 0 &&
        eloMessage.informationsAppairage.typeCamion === false &&
        eloMessage.informationsAppairage.estPostal === false && 
        eloMessage.informationsAppairage.estTIRATA === false && 
        eloMessage.informationsAppairage.estEmballageVide === false
      ) {
        return "At least one ICS2 ENS (Entry Summary Declaration) is required for Entering France (Import).";
      }
    }

    if (currentDirection === "EXPORT") {
      if (
        getSafeArray(eloMessage.identifiantsDeclaration).length === 0 &&
        eloMessage.informationsAppairage.typeCamion !== true &&
        eloMessage.informationsAppairage.estPostal !== true && 
        eloMessage.informationsAppairage.estTIRATA !== true && 
        eloMessage.informationsAppairage.estEmballageVide !== true
      ) {
        return "At least one Export MRN / Transit document (T1/T2) is required for Exiting France (Export).";
      }
    }

    return null;
  };

  const handleSubmit = () => {
    setLoading(true);
    setFormDataErrors(null);

    const validationError = validateBeforeSubmit();
    if (validationError) {
      setFormDataErrors(validationError);
      setPopup({
        open: true,
        message: validationError,
        severity: "error",
      });
      setLoading(false);
      return;
    }

    const payload = {
      ...eloMessage,
      identifiantsDeclaration: getSafeArray(eloMessage.identifiantsDeclaration),
      ics2EnsDeclarations: getSafeArray(eloMessage.ics2EnsDeclarations),
      importTransitDeclarations: getSafeArray(
        eloMessage.importTransitDeclarations,
      ),
    };

    const request = isEditMode
      ? EloMessageRepository.update(id, payload)
      : EloMessageRepository.create(payload);

    request
      .then(() => {
        dispatch(triggerRerender());
        setPopup({
          open: true,
          message: "Saved successfully!",
          severity: "success",
        });
        navigateBack();
      })
      .catch((err) => {
        console.error(err);
        const resolvedError = ErrorMessageResolver.resolve(err);
        setFormDataErrors(resolvedError);
        setPopup({
          open: true,
          message: resolvedError || "Something went wrong",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const reject = () => {
    setLoading(true);
    EloMessageRepository.setRejected(id)
      .then((response) => {
        dispatch(notifyShowSuccessMessage("Declaration rejected successfully"));
        setLoading(false);
        navigateBack();
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setLoading(false);
      });
  };

  console.log('loading:', loading, 'status:', eloMessage?.status);

  return (
    <>
      <Dialog open maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "20px",
              flexWrap: "wrap",
              gap: 1,
              pr: 1,
            }}
          >
            <Icon color="primary" fontSize="large">
              assignment
            </Icon>

            <span style={{ color: "#323232", lineHeight: "1.2em" }}>
              {isEditMode ? "Elo Message" : "New Elo Message"}
            </span>

            {eloMessage?.status && (
              <Chip
                label={eloMessage.status}
                size="small"
                color={
                  eloMessage?.status === "CREATED"
                    ? "primary"
                    : eloMessage?.status === "SENT"
                      ? "primary"
                      : eloMessage?.status === "ERROR"
                        ? "error"
                        : eloMessage?.status === "COMPLETED"
                          ? "success"
                          : "primary"
                }
              />
            )}

            {eloMessage?.eloNumber && (
              <Chip
                label={`Elo Number: ${eloMessage.eloNumber}`}
                color="info"
                variant="outlined"
                size="small"
              />
            )}

            {eloMessage?.referenceNumber && (
              <Chip
                label={`Reference Number: ${eloMessage.referenceNumber}`}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {alerts.filter((alert) => alert.severity === "error").map((alert) => (
            <Alert severity={alert.severity} key={alert.id} sx={{ mb: 2 }}>
              {alert.description}
            </Alert>
          ))}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {Array.isArray(eloMessage?.errors) &&
                eloMessage.errors.length > 0 &&
                eloMessage.errors.map((error, index) => (
                  <Box key={`${error?.code || "error"}-${index}`} mb={1}>
                    {index === 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <AlertTitle>Note</AlertTitle>
                        Please check and correct the following issues before
                        saving the elo message.
                      </Alert>
                    )}

                    <Alert severity="error">
                      <AlertTitle>{error?.code || "Error"}</AlertTitle>

                      {error?.translatedDescription && (
                        <>
                          <strong>English:</strong>{" "}
                          {error.translatedDescription}
                        </>
                      )}

                      {error?.description && (
                        <>
                          <br />
                          <strong>French:</strong> {error.description}
                        </>
                      )}
                    </Alert>
                  </Box>
                ))}

              {formDataErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formDataErrors}
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>General Details</SectionHeader>
            </Grid>

            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                disabled
                options={[]}
                value={eloMessage.organization || null}
                inputValue={organizationSearch}
                getOptionLabel={(option) => option?.nameEn || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    fullWidth
                    size="small"
                    sx={inputSx}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Truck Plate Number"
                value={eloMessage.internalReference || ""}
                onChange={(e) =>
                  handleChangeEloMessage(
                    "internalReference",
                    e.target.value
                      .trim()
                      .replace(/[^a-z0-9]/gi, "")
                      .toUpperCase(),
                  )
                }
                fullWidth
                size="small"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>Vehicle Details</SectionHeader>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={currentDirection}
                  onChange={(e) => handleDirectionChange(e.target.value)}
                  label="Direction"
                  sx={inputSx}
                >
                  <MenuItem value="IMPORT">Entering France (Import)</MenuItem>
                  <MenuItem value="EXPORT">Exiting France (Export)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <FieldTitle hint="Vehicle and goods attributes">
                  Options
                </FieldTitle>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    pt: 0.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={false}
                        checked={Boolean(
                          eloMessage.informationsAppairage?.estTIRATA,
                        )}
                        onChange={(e) => {
                          console.log('rendering options, isEditMode:', isEditMode);
                          const checked = e.target.checked;
                          setEloMessage((prev) => ({
                            ...prev,
                            informationsAppairage: {
                              ...prev.informationsAppairage,
                              estTIRATA: checked,
                            },
                            ...(checked && {
                              ics2EnsDeclarations: [],
                              importTransitDeclarations: [],
                              identifiantsDeclaration: [],
                            }),
                          }));
                        }}
                      />
                    }
                    label="TIR/ATA"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage?.estSPS,
                        )}
                        onChange={(e) =>
                          handleChangeEloMessage(
                            "informationsAppairage.estSPS",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Sanitary / Phytosanitary"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage?.typeCamion,
                        )}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEloMessage((prev) => ({
                            ...prev,
                            informationsAppairage: {
                              ...prev.informationsAppairage,
                              typeCamion: checked,
                            },
                            ...(checked && {
                              ics2EnsDeclarations: [],
                              importTransitDeclarations: [],
                              identifiantsDeclaration: [],
                            }),
                          }));
                        }}
                      />
                    }
                    label="Empty Vehicle"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage?.estProduitPeche,
                        )}
                        onChange={(e) =>
                          handleChangeEloMessage(
                            "informationsAppairage.estProduitPeche",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Fish Products"
                  />

                  {currentDirection === "IMPORT" && <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage?.estPostal,
                        )}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEloMessage((prev) => ({
                            ...prev,
                            informationsAppairage: {
                              ...prev.informationsAppairage,
                              estPostal: checked,
                            },
                            ...(checked && {
                              ics2EnsDeclarations: [],
                              importTransitDeclarations: [],
                              identifiantsDeclaration: [],
                            }),
                          }));
                        }}
                      />
                    }
                    label="Is postal"
                  />}

                  {currentDirection === "IMPORT" && <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage?.estEmballageVide,
                        )}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEloMessage((prev) => ({
                            ...prev,
                            informationsAppairage: {
                              ...prev.informationsAppairage,
                              estEmballageVide: checked,
                            },
                            ...(checked && {
                              ics2EnsDeclarations: [],
                              importTransitDeclarations: [],
                              identifiantsDeclaration: [],
                            }),
                          }));
                        }}
                      />
                    }
                    label="Is empty packaging"
                  />}

                  {currentDirection === "IMPORT" && <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(
                          eloMessage.informationsAppairage
                            ?.possedeContratTransport,
                        )}
                        onChange={(e) =>
                          handleChangeEloMessage(
                            "informationsAppairage.possedeContratTransport",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Owns Transport Contract"
                  />}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader>Declaration Identifiers</SectionHeader>
            </Grid>

            {currentDirection === "IMPORT" ? (
              <>
                <Grid item xs={12} md={6}>
                  <IdentifierSection
                    title="ICS2 ENS (Entry Summary Declaration)"
                    hint="Mandatory. At least one entry is required for Entering France (Import)."
                    currentValue={importEnsInput}
                    setCurrentValue={setImportEnsInput}
                    disabled={isEmptyChecked || isPostalChecked || isAtaChecked || isEmptyPackaging}
                    items={getSafeArray(eloMessage.ics2EnsDeclarations)}
                    onAdd={() =>
                      addToArrayField(
                        "ics2EnsDeclarations",
                        importEnsInput,
                        setImportEnsInput,
                      )
                    }
                    onRemove={(index) =>
                      removeFromArrayField("ics2EnsDeclarations", index)
                    }
                    helperTextEmpty="Mandatory section."
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <IdentifierSection
                    title="Import MRNs / Transit documents (T1/T2)"
                    hint="Optional section."
                    currentValue={importTransitInput}
                    setCurrentValue={setImportTransitInput}
                    disabled={isEmptyChecked || isPostalChecked || isAtaChecked || isEmptyPackaging}
                    items={getSafeArray(eloMessage.importTransitDeclarations)}
                    onAdd={() =>
                      addToArrayField(
                        "importTransitDeclarations",
                        importTransitInput,
                        setImportTransitInput,
                      )
                    }
                    onRemove={(index) =>
                      removeFromArrayField("importTransitDeclarations", index)
                    }
                    helperTextEmpty="Optional section."
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <IdentifierSection
                  title="Export MRNs / Transit documents (T1/T2)"
                  hint="Mandatory. At least one entry is required for Exiting France (Export)."
                  currentValue={exportInput}
                  setCurrentValue={setExportInput}
                  disabled={isEmptyChecked || isPostalChecked || isAtaChecked || isEmptyPackaging} 
                  items={getSafeArray(eloMessage.identifiantsDeclaration)}
                  onAdd={() =>
                    addToArrayField(
                      "identifiantsDeclaration",
                      exportInput,
                      setExportInput,
                    )
                  }
                  onRemove={(index) =>
                    removeFromArrayField("identifiantsDeclaration", index)
                  }
                  helperTextEmpty="Mandatory section."
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleDuplicateDialogOpen}
            color="info"
            startIcon={<Icon>file_copy</Icon>}
            style={{ marginRight: "20px" }}
            disabled={
              loading ||
              !eloMessage ||
              !allowedStatusesDuplicate.includes(eloMessage.status)
            }
          >
            Duplicate
          </Button>
          <Button
            onClick={reject}
            color="error"
            startIcon={<Icon>save</Icon>}
            style={{ marginRight: "20px" }}
            disabled={loading || !["ERROR", "DRAFT"].includes(eloMessage?.status)}
          >
            Reject
          </Button>
          {(!eloMessage?.status || eloMessage?.status === "DRAFT") && (
            <Button
              onClick={handleDraft}
              color="primary"
              style={{ marginRight: "20px" }}
              startIcon={<Icon>save</Icon>}
              disabled={loading}
            >
              Draft
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            color="success"
            startIcon={<SaveIcon />}
            style={{ marginRight: "20px" }}
            disabled={
              loading ||
              hasErrorAlert ||
              eloMessage?.status === "SENT" ||
              eloMessage?.status === "CREATED" ||
              eloMessage?.status === "PAIRED" ||
              eloMessage?.status === "EMBARKED" ||
              eloMessage?.status === "DEBARKED" ||
              eloMessage?.status === "COMPLETED" ||
              eloMessage?.status === "REJECTED"
            }
          >
            {loading ? "Saving..." : "Save"}
          </Button>
          <Button
            onClick={handleClose}
            color="error"
            startIcon={<Icon>close</Icon>}
            disabled={loading}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDuplicateDialog}
        onClose={handleCloseDuplicateDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2.5,
            minWidth: 420,
            boxShadow: 8,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
          }}
        >
          <Icon color="info" sx={{ fontSize: 28 }}>
            file_copy
          </Icon>
          <Typography variant="h6" fontWeight={600}>
            Duplicate declaration
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to duplicate the declaration ?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "space-between",
            pt: 3,
          }}
        >
          <Button
            onClick={handleCloseDuplicateDialog}
            variant="outlined"
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            color="info"
            variant="contained"
            onClick={duplicateDeclaration}
            sx={{
              borderRadius: 2,
              minWidth: 120,
              fontWeight: 600,
            }}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={popup.open}
        autoHideDuration={4000}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
          severity={popup.severity}
          sx={{ width: "100%" }}
        >
          {popup.message}
        </Alert>
      </Snackbar>
    </>
  );
};
